<script setup lang="ts">
import { notification } from 'ant-design-vue';
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  PaperClipOutlined,
} from '@ant-design/icons-vue';
import axios from 'axios';
import { computed, onUnmounted, reactive, ref } from 'vue';
import { get as obterManagedJob } from '~/api/generated/clients/managed-jobs/get';
import { API_URL } from '~/utils';

interface Anexo {
  referenciaDocumento: string;
  referenciaDocumentoNome: string;
  caminhoArquivo: string;
}

interface LinhaExcel {
  gcpj: string;
  referencia: string;
  referenciaNome: string;
  andamento: string;
  anexosString: string;
  anexos: Anexo[];
}

interface AnexoEnviado {
  nomeArquivo: string;
  referenciaNome: string;
}

interface LinhaProcessada {
  gcpj: string;
  pastaCodigo: string;
  pastaNome: string;
  referenciaCodigo: string;
  referenciaNome: string;
  andamento: string;
  anexosEnviados: AnexoEnviado[];
}

interface ErroValidacao {
  numeroLinha: number;
  gcpj: string;
  mensagem: string;
}

interface ArquivoZip {
  arquivo: string;
}

interface ResultadoImportacao {
  linhasExcel: LinhaExcel[];
  arquivosZip: ArquivoZip[];
  linhasProcessadas: LinhaProcessada[];
  errosValidacao: ErroValidacao[];
}

interface JobResponse {
  status: number;
  progresso?: number;
  resultadoJsonDocument?: ResultadoImportacao;
}

const emit = defineEmits(['close']);

const formRef = ref<any>(null);
const planilhaRef = ref<any>(null);
const arquivosZipRef = ref<any>(null);
const status = ref<'idle' | 'uploading' | 'loading' | 'success' | 'error'>('idle');
const jobId = ref<string | null>(null);
let pollIntervalId: ReturnType<typeof setInterval> | null = null;

const model = reactive<{
  planilha: any;
  arquivosZip: any;
}>({
  planilha: null,
  arquivosZip: null,
});

const progress = ref(0);
const resultado = ref<ResultadoImportacao | null>(null);
const jobStatus = ref<number | null>(null);

const isSuccess = computed(() => jobStatus.value === 2);
const isFailed = computed(() => jobStatus.value === 3);

const totalProcessadas = computed(() => resultado.value?.linhasProcessadas?.length ?? 0);
const totalErros = computed(() => resultado.value?.errosValidacao?.length ?? 0);
const totalLinhasExcel = computed(() => resultado.value?.linhasExcel?.length ?? 0);
const totalArquivosZip = computed(() => resultado.value?.arquivosZip?.length ?? 0);

const resultStatus = computed(() => {
  if (isFailed.value) return 'error';
  if (isSuccess.value && totalErros.value === 0) return 'success';
  if (isSuccess.value && totalErros.value > 0 && totalProcessadas.value > 0) return 'warning';
  if (isSuccess.value && totalProcessadas.value === 0 && totalErros.value > 0) return 'error';
  return 'success';
});

const resultTitle = computed(() => {
  if (isFailed.value) return 'Erro na importacao';
  if (isSuccess.value && totalErros.value === 0 && totalProcessadas.value > 0) return 'Importacao realizada com sucesso';
  if (isSuccess.value && totalErros.value > 0 && totalProcessadas.value > 0) return 'Importacao parcial';
  if (isSuccess.value && totalProcessadas.value === 0 && totalErros.value > 0) return 'Nenhuma linha processada';
  if (isSuccess.value && totalProcessadas.value === 0 && totalErros.value === 0) return 'Processamento concluido';
  return 'Processamento concluido';
});

const resultSubTitle = computed(() => {
  const parts: string[] = [];
  if (totalLinhasExcel.value > 0) {
    parts.push(`${totalLinhasExcel.value} linha(s) no Excel`);
  }
  if (totalArquivosZip.value > 0) {
    parts.push(`${totalArquivosZip.value} arquivo(s) no ZIP`);
  }
  if (totalProcessadas.value > 0) {
    parts.push(`${totalProcessadas.value} processada(s) com sucesso`);
  }
  if (totalErros.value > 0) {
    parts.push(`${totalErros.value} erro(s)`);
  }
  return parts.join(' | ');
});

async function pollJobStatus() {
  if (!jobId.value) return;

  try {
    const job = (await obterManagedJob(jobId.value)) as unknown as JobResponse;

    if (job.progresso != null) {
      progress.value = Math.round(job.progresso * 100);
    }

    if (job.resultadoJsonDocument) {
      resultado.value = job.resultadoJsonDocument;
    }

    // Status: 1 = Enfileirado, 2 = Sucesso, 3 = Falha, 4 = Cancelado, 5 = Em Cancelamento, 6 = Processando
    if (![1, 5, 6].includes(job.status)) {
      stopPolling();
      jobStatus.value = job.status;
      status.value = 'success';
    }
  }
  catch {
    // Continua tentando
  }
}

function startPolling() {
  pollJobStatus();
  pollIntervalId = setInterval(pollJobStatus, 2000);
}

function stopPolling() {
  if (pollIntervalId) {
    clearInterval(pollIntervalId);
    pollIntervalId = null;
  }
}

onUnmounted(() => {
  stopPolling();
});

async function submit() {
  try {
    await formRef.value.validate();

    status.value = 'uploading';

    // Upload dos arquivos ANTES de mudar para loading
    await planilhaRef.value.upload();
    if (model.arquivosZip) {
      await arquivosZipRef.value.upload();
    }

    status.value = 'loading';

    // Enviar para o endpoint
    const response = await axios.post(
      `${API_URL}/atualizacao-gcpj/v1/importar`,
      {
        planilha: model.planilha,
        arquivosZip: model.arquivosZip,
      },
      { getAccessToken: true, timeout: 120000 } as any,
    );

    jobId.value = response.data.jobId;

    // Iniciar polling para aguardar o job finalizar
    startPolling();
  }
  catch (error: any) {
    status.value = 'error';

    const errorMessage = error?.response?.data?.errorMessage
      || 'Erro ao processar a importacao';

    notification.error({
      message: errorMessage,
      duration: 10,
    });
  }
}
</script>

<template>
  <div style="padding-bottom: 24px">
    <DrawerTitle2 title="Importar Atualizacoes GCPJ" @close="() => emit('close')" />

    <div style="padding: 0 24px">
      <!-- Formulario - quando idle ou uploading -->
      <a-form
        v-if="status === 'idle' || status === 'uploading'"
        ref="formRef"
        :model="model"
        layout="vertical"
      >
        <a-collapse style="margin-bottom: 24px">
          <a-collapse-panel key="1" header="GUIA DE COMO PREENCHER A PLANILHA EXCEL">
            <div>
              <h4>Estrutura da Planilha</h4>
              <p>A planilha deve conter as seguintes colunas:</p>
              <ul>
                <li><strong>GCPJ</strong> - Número do GCPJ</li>
                <li><strong>REFERENCIA</strong> - Referência do andamento</li>
                <li><strong>ANDAMENTO</strong> - Texto do andamento</li>
                <li><strong>ANEXOS</strong> - Lista de anexos no formato específico</li>
              </ul>
            </div>
            <a-divider />
            <div>
              <h4>Formato da Coluna Anexos</h4>
              <p>Os anexos devem seguir o formato:</p>
              <code>referencia_documento|pasta/arquivo.txt;referencia_documento1|pasta/arquivo1.txt</code>
              <p style="margin-top: 8px">Cada anexo é separado por ponto e vírgula (;)</p>
              <p>Cada anexo tem o formato: <code>referencia|caminho_no_zip</code></p>
            </div>
          </a-collapse-panel>

          <a-collapse-panel key="2" header="GUIA DE COMO CRIAR O ARQUIVO ZIP">
            <div>
              <h4>Estrutura do ZIP</h4>
              <p>O arquivo ZIP deve conter pastas organizadas por GCPJ:</p>
              <pre style="background: #f5f5f5; padding: 12px; border-radius: 4px">
gcpj1/
  arquivo1.pdf
  arquivo2.pdf
gcpj2/
  documento.pdf
  anexo.pdf</pre>
            </div>
            <a-divider />
            <div>
              <h4>Importante</h4>
              <ul>
                <li>O caminho do arquivo no ZIP deve corresponder ao valor na coluna "anexos" da planilha</li>
                <li>Os nomes das pastas devem ser os números de GCPJ</li>
                <li>Os arquivos podem estar em subpastas dentro da pasta do GCPJ</li>
              </ul>
            </div>
          </a-collapse-panel>
        </a-collapse>

        <a-form-item
          label="Planilha Excel"
          name="planilha"
          :rules="{
            message: 'Planilha obrigatória',
            required: true,
            trigger: 'change',
            type: 'object',
          }"
        >
          <UploadFile
            ref="planilhaRef"
            v-model:file="model.planilha"
          />
        </a-form-item>

        <a-form-item
          label="Arquivo ZIP com anexos (opcional)"
          name="arquivosZip"
        >
          <UploadFile
            ref="arquivosZipRef"
            v-model:file="model.arquivosZip"
          />
        </a-form-item>

        <a-form-item>
          <a-button
            block
            type="primary"
            size="large"
            :loading="status === 'uploading'"
            :disabled="status === 'uploading'"
            @click="submit"
          >
            {{ status === 'uploading' ? 'Enviando arquivos...' : 'Importar' }}
          </a-button>
        </a-form-item>
      </a-form>

      <!-- Estado loading - barra de progresso + resultados parciais -->
      <template v-if="status === 'loading'">
        <div style="margin-bottom: 24px">
          <a-progress :percent="progress" status="active" />
        </div>

        <div v-if="!resultado" style="text-align: center; padding: 24px; color: #999">
          <LoadingOutlined style="font-size: 24px; margin-bottom: 8px" />
          <div>Processando importação...</div>
        </div>

        <!-- Mostrar resultados parciais durante o loading -->
        <template v-if="resultado">
          <div style="text-align: center; margin-bottom: 16px; color: #999">
            <LoadingOutlined style="margin-right: 8px" />
            <span>Processando... {{ totalProcessadas }} de {{ totalLinhasExcel }} linha(s)</span>
          </div>

          <a-tabs style="text-align: left">
              <!-- Tab: Linhas do Excel -->
              <a-tab-pane key="excel" :tab="`Linhas do Excel (${totalLinhasExcel})`">
                <template v-if="totalLinhasExcel > 0">
                  <a-table
                    size="small"
                    :data-source="resultado?.linhasExcel"
                    :pagination="false"
                    :scroll="{ y: 300 }"
                    bordered
                  >
                    <a-table-column title="GCPJ" data-index="gcpj" :width="120">
                      <template #default="{ text }">
                        <span style="font-weight: 500">{{ text }}</span>
                      </template>
                    </a-table-column>
                    <a-table-column title="Referência" :width="250">
                      <template #default="{ record }">
                        <div>{{ record.referenciaNome || record.referencia }}</div>
                      </template>
                    </a-table-column>
                    <a-table-column title="Andamento" data-index="andamento" :ellipsis="true" />
                    <a-table-column title="Anexos" data-index="anexosString" :ellipsis="true" />
                  </a-table>
                </template>
                <a-empty v-else description="Nenhuma linha encontrada no Excel" />
              </a-tab-pane>

              <!-- Tab: Arquivos do ZIP -->
              <a-tab-pane key="zip" :tab="`Arquivos do ZIP (${totalArquivosZip})`">
                <template v-if="totalArquivosZip > 0">
                  <a-table
                    size="small"
                    :data-source="resultado?.arquivosZip"
                    :pagination="false"
                    :scroll="{ y: 300 }"
                    bordered
                  >
                    <a-table-column title="Arquivo" data-index="arquivo" />
                  </a-table>
                </template>
                <a-empty v-else description="Nenhum arquivo encontrado no ZIP" />
              </a-tab-pane>

              <!-- Tab: Processados com sucesso -->
              <a-tab-pane key="sucesso">
                <template #tab>
                  <span style="color: #52c41a">
                    <CheckCircleOutlined style="margin-right: 4px" />
                    Sucesso ({{ totalProcessadas }})
                  </span>
                </template>
                <template v-if="totalProcessadas > 0">
                  <a-list
                    size="small"
                    :data-source="resultado?.linhasProcessadas"
                    bordered
                    style="max-height: 400px; overflow-y: auto"
                  >
                    <template #renderItem="{ item }">
                      <a-list-item style="display: block">
                        <div style="color: #444; font-size: 14px; margin-bottom: 8px">
                          <strong>Pasta: </strong> 
                          <a :href="`/pasta/${item.pastaCodigo}`" target="_blank" style="font-weight: 600; font-size: 15px">{{ item.pastaNome }}</a>
                        </div>
                        <div style="color: #444; font-size: 14px; margin-bottom: 8px">
                          <strong>GCPJ: </strong> 
                          {{ item.gcpj }}
                        </div>
                        <div style="color: #444; font-size: 14px; margin-bottom: 6px">
                          <strong>Referencia:</strong> {{ item.referenciaNome }}
                        </div>
                        <div v-if="item.andamento" style="color: #444; font-size: 14px; margin-bottom: 6px">
                          <strong>Andamento:</strong> {{ item.andamento }}
                        </div>
                        <div v-if="item.anexosEnviados?.length > 0" style="margin-top: 8px">
                          <div style="font-size: 14px; color: #444; margin-bottom: 4px">
                            <PaperClipOutlined style="margin-right: 4px" />
                            <strong>Anexos enviados:</strong>
                          </div>
                          <div
                            v-for="(anexo, idx) in item.anexosEnviados"
                            :key="idx"
                            style="font-size: 13px; color: #666; padding-left: 16px"
                          >
                            {{ anexo.nomeArquivo }} ({{ anexo.referenciaNome }})
                          </div>
                        </div>
                      </a-list-item>
                    </template>
                  </a-list>
                </template>
                <a-empty v-else description="Nenhuma linha processada com sucesso" />
              </a-tab-pane>

              <!-- Tab: Erros -->
              <a-tab-pane key="erros">
                <template #tab>
                  <span :style="{ color: totalErros > 0 ? '#ff4d4f' : undefined }">
                    <ExclamationCircleOutlined style="margin-right: 4px" />
                    Erros ({{ totalErros }})
                  </span>
                </template>
                <template v-if="totalErros > 0">
                  <a-table
                    size="small"
                    :data-source="resultado?.errosValidacao"
                    :pagination="false"
                    :scroll="{ y: 300 }"
                    bordered
                  >
                    <a-table-column title="Linha" data-index="numeroLinha" :width="80" />
                    <a-table-column title="GCPJ" data-index="gcpj" :width="150">
                      <template #default="{ text }">
                        <span v-if="text" style="font-weight: 500">{{ text }}</span>
                        <span v-else style="color: #999">-</span>
                      </template>
                    </a-table-column>
                    <a-table-column title="Erro" data-index="mensagem">
                      <template #default="{ text }">
                        <span style="color: #ff4d4f">{{ text }}</span>
                      </template>
                    </a-table-column>
                  </a-table>
                </template>
                <a-empty v-else description="Nenhum erro encontrado" />
              </a-tab-pane>
            </a-tabs>
        </template>
      </template>

      <!-- Estado success - resultado final com tabs -->
      <template v-if="status === 'success'">
        <a-result
          :status="resultStatus"
          :title="resultTitle"
          :sub-title="resultSubTitle"
        >
          <template #extra>
            <a-tabs style="text-align: left">
              <!-- Tab: Linhas do Excel -->
              <a-tab-pane key="excel" :tab="`Linhas do Excel (${totalLinhasExcel})`">
                <template v-if="totalLinhasExcel > 0">
                  <a-table
                    size="small"
                    :data-source="resultado?.linhasExcel"
                    :pagination="false"
                    :scroll="{ y: 300 }"
                    bordered
                  >
                    <a-table-column title="GCPJ" data-index="gcpj" :width="120">
                      <template #default="{ text }">
                        <span style="font-weight: 500">{{ text }}</span>
                      </template>
                    </a-table-column>
                    <a-table-column title="Referência" :width="250">
                      <template #default="{ record }">
                        <div>{{ record.referenciaNome || record.referencia }}</div>
                      </template>
                    </a-table-column>
                    <a-table-column title="Andamento" data-index="andamento" :ellipsis="true" />
                    <a-table-column title="Anexos" data-index="anexosString" :ellipsis="true" />
                  </a-table>
                </template>
                <a-empty v-else description="Nenhuma linha encontrada no Excel" />
              </a-tab-pane>

              <!-- Tab: Arquivos do ZIP -->
              <a-tab-pane key="zip" :tab="`Arquivos do ZIP (${totalArquivosZip})`">
                <template v-if="totalArquivosZip > 0">
                  <a-table
                    size="small"
                    :data-source="resultado?.arquivosZip"
                    :pagination="false"
                    :scroll="{ y: 300 }"
                    bordered
                  >
                    <a-table-column title="Arquivo" data-index="arquivo" />
                  </a-table>
                </template>
                <a-empty v-else description="Nenhum arquivo encontrado no ZIP" />
              </a-tab-pane>

              <!-- Tab: Processados com sucesso -->
              <a-tab-pane key="sucesso">
                <template #tab>
                  <span style="color: #52c41a">
                    <CheckCircleOutlined style="margin-right: 4px" />
                    Sucesso ({{ totalProcessadas }})
                  </span>
                </template>
                <template v-if="totalProcessadas > 0">
                  <a-list
                    size="small"
                    :data-source="resultado?.linhasProcessadas"
                    bordered
                    style="max-height: 400px; overflow-y: auto"
                  >
                    <template #renderItem="{ item }">
                      <a-list-item style="display: block">
                        <div style="margin-bottom: 8px">
                          <a :href="`/pasta/${item.pastaCodigo}`" target="_blank" style="font-weight: 600; font-size: 15px">
                            GCPJ: {{ item.gcpj }}
                          </a>
                          <span style="color: #666; margin-left: 8px">{{ item.pastaNome }}</span>
                        </div>
                        <div style="color: #444; font-size: 14px; margin-bottom: 6px">
                          <strong>Referencia:</strong> {{ item.referenciaNome }}
                        </div>
                        <div v-if="item.andamento" style="color: #444; font-size: 14px; margin-bottom: 6px">
                          <strong>Andamento:</strong> {{ item.andamento }}
                        </div>
                        <div v-if="item.anexosEnviados?.length > 0" style="margin-top: 8px">
                          <div style="font-size: 14px; color: #444; margin-bottom: 4px">
                            <PaperClipOutlined style="margin-right: 4px" />
                            <strong>Anexos enviados:</strong>
                          </div>
                          <div
                            v-for="(anexo, idx) in item.anexosEnviados"
                            :key="idx"
                            style="font-size: 13px; color: #666; padding-left: 16px"
                          >
                            {{ anexo.nomeArquivo }} ({{ anexo.referenciaNome }})
                          </div>
                        </div>
                      </a-list-item>
                    </template>
                  </a-list>
                </template>
                <a-empty v-else description="Nenhuma linha processada com sucesso" />
              </a-tab-pane>

              <!-- Tab: Erros -->
              <a-tab-pane key="erros">
                <template #tab>
                  <span :style="{ color: totalErros > 0 ? '#ff4d4f' : undefined }">
                    <ExclamationCircleOutlined style="margin-right: 4px" />
                    Erros ({{ totalErros }})
                  </span>
                </template>
                <template v-if="totalErros > 0">
                  <a-table
                    size="small"
                    :data-source="resultado?.errosValidacao"
                    :pagination="false"
                    :scroll="{ y: 300 }"
                    bordered
                  >
                    <a-table-column title="Linha" data-index="numeroLinha" :width="80" />
                    <a-table-column title="GCPJ" data-index="gcpj" :width="150">
                      <template #default="{ text }">
                        <span v-if="text" style="font-weight: 500">{{ text }}</span>
                        <span v-else style="color: #999">-</span>
                      </template>
                    </a-table-column>
                    <a-table-column title="Erro" data-index="mensagem">
                      <template #default="{ text }">
                        <span style="color: #ff4d4f">{{ text }}</span>
                      </template>
                    </a-table-column>
                  </a-table>
                </template>
                <a-empty v-else description="Nenhum erro encontrado" />
              </a-tab-pane>
            </a-tabs>

            <div style="margin-top: 16px; text-align: center">
              <a-button type="primary" @click="() => emit('close')">Fechar</a-button>
            </div>
          </template>
        </a-result>
      </template>
    </div>
  </div>
</template>
